import { OptionsApi } from '@core/swagger-client';
import { ErrorMessagePage, LoaderPage } from '@core/ui';
import { useLocation, useNavigate, useParams } from '@online-webinar/router';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { AccountWizardState } from '../../../types';
import { AuthPageTitle } from '../../atoms';
import { RegisterForBroadcastInfo } from '../../molecules';
import { AuthPage, AuthPageContent, Login, RegisterSideContent } from '../../organism';

export const LoginPage = () => {
    const { webinarId } = useParams({ strict: false });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    useAuthRedirect();

    const step = (location.state?.step as 'email' | 'password') ?? 'email';
    const state = location.state?.wizard as AccountWizardState | undefined;

    const { isLoading, data: webinar, error } = useQuery(OptionsApi.webinar.get(webinarId!, { enabled: !!webinarId, staleTime: 60_000 }));

    const onStepChange = useCallback(
        (newStep: 'email' | 'password', newState: AccountWizardState) => {
            if (step == newStep) return;

            const nextState = { ...location.state, wizard: newState, step: newStep };
            if (webinarId) {
                navigate({ to: '/$webinarId/login', params: { webinarId }, state: nextState });
            } else {
                navigate({ to: '/login', state: nextState });
            }
        },
        [step, webinarId]
    );

    const onRegister = useCallback(
        (emailAddress: string) => {
            const nextState = { ...location.state, wizard: { emailAddress } };
            if (webinarId) {
                navigate({ to: '/$webinarId/register', params: { webinarId }, state: nextState });
            } else {
                navigate({ to: '/register', state: nextState });
            }
        },
        [webinarId]
    );

    const onForgotPassword = useCallback(
        (emailAddress: string) => {
            const nextState = { ...location.state, wizard: { emailAddress } };
            if (webinarId) {
                navigate({ to: '/$webinarId/forgot-password', params: { webinarId }, state: nextState });
            } else {
                navigate({ to: '/forgot-password', state: nextState });
            }
        },
        [webinarId]
    );

    if (error) return <ErrorMessagePage apiError={error} />;
    if (isLoading) return <LoaderPage />;

    return (
        <AuthPage>
            <AuthPageContent>
                {webinar && <RegisterForBroadcastInfo webinar={webinar} />}
                <AuthPageTitle className="mb-8">{t('Pages.Login.Header')}</AuthPageTitle>
                <Login state={state} step={step} onStepChange={onStepChange} onRegister={onRegister} onForgotPassword={onForgotPassword} />
            </AuthPageContent>
            <RegisterSideContent />
        </AuthPage>
    );
};
